import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Github, Twitter, Mail, Heart } from 'lucide-react';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const footerLinks = [
    {
      title: t('footer.product.title'),
      links: [
        { label: t('nav.features'), to: '/features' },
        { label: t('footer.product.pricing'), to: '/pricing' },
        { label: t('footer.product.api'), to: '/api' },
        { label: t('footer.product.integrations'), to: '/integrations' },
      ],
    },
    {
      title: t('footer.company.title'),
      links: [
        { label: t('footer.company.about'), to: '/about' },
        { label: t('footer.company.blog'), to: '/blog' },
        { label: t('footer.company.careers'), to: '/careers' },
        { label: t('footer.company.contact'), to: '/contact' },
      ],
    },
    {
      title: t('footer.support.title'),
      links: [
        { label: t('footer.support.helpCenter'), to: '/help' },
        { label: t('footer.support.documentation'), to: '/docs' },
        { label: t('footer.support.status'), to: '/status' },
        { label: t('footer.support.community'), to: '/community' },
      ],
    },
    {
      title: t('footer.legal.title'),
      links: [
        { label: t('footer.legal.privacy'), to: '/privacy' },
        { label: t('footer.legal.terms'), to: '/terms' },
        { label: t('footer.legal.cookies'), to: '/cookies' },
        { label: t('footer.legal.gdpr'), to: '/gdpr' },
      ],
    },
  ];

  return (
    <footer className="bg-white dark:bg-zinc-900 border-t border-zinc-200 dark:border-zinc-700">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-8">
          {/* Brand Section */}
          <div className="lg:col-span-2">
            <Link to="/" className="flex items-center gap-x-2 mb-4">
              <img
                src="/logo_256.png"
                alt="Meet Logo"
                className="size-8"
              />
              <img
                src="/MEET.png"
                alt="Meet"
                className="h-6"
              />
            </Link>
            <p className="text-zinc-600 dark:text-zinc-300 mb-6 max-w-sm">
              {t('footer.description')}
            </p>
            <div className="flex gap-x-4">
              <a
                href="https://github.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors duration-200"
              >
                <Github className="size-5" />
              </a>
              <a
                href="https://twitter.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors duration-200"
              >
                <Twitter className="size-5" />
              </a>
              <a
                href="mailto:hello@meet.com"
                className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 transition-colors duration-200"
              >
                <Mail className="size-5" />
              </a>
            </div>
          </div>

          {/* Links Sections */}
          {footerLinks.map((section) => (
            <div key={section.title}>
              <h3 className="text-sm font-semibold text-zinc-900 dark:text-white uppercase tracking-wider mb-4">
                {section.title}
              </h3>
              <ul className="space-y-3">
                {section.links.map((link) => (
                  <li key={link.to}>
                    <Link
                      to={link.to}
                      className="text-zinc-600 dark:text-zinc-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors duration-200 text-sm"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Section */}
        <div className="mt-12 pt-8 border-t border-zinc-200 dark:border-zinc-700">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-zinc-500 dark:text-zinc-400 text-sm">
              {t('footer.copyright', { year: currentYear })}
            </p>
            <div className="flex items-center gap-x-1 text-zinc-500 dark:text-zinc-400 text-sm mt-4 md:mt-0">
              <span>{t('footer.madeWith')}</span>
              <Heart className="size-4 text-red-500" />
              <span>{t('footer.forBetterMeetings')}</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
